import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from './cx';
import type { Tone } from '../lib/types';
import './components.css';

export interface ToastInput {
  title: string;
  message?: string;
  tone?: Tone;
  /** Milliseconds on screen. `0` keeps it until dismissed. Default 4200. */
  duration?: number;
}

interface ToastItem extends ToastInput { id: number }

const Ctx = createContext<((t: ToastInput) => void) | null>(null);

/** Wraps the app once, in AppShell. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: ToastInput) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { ...t, id }]);
    const ms = t.duration ?? 4200;
    if (ms > 0) setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), ms);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="k-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={cx('k-toast', `k-toast--${t.tone ?? 'neutral'}`)}>
            <div style={{ minWidth: 0 }}>
              <div className="k-toast__title">{t.title}</div>
              {t.message && <div className="k-toast__msg">{t.message}</div>}
            </div>
            <button
              type="button"
              className="k-icon-btn"
              style={{ width: 26, height: 26, marginLeft: 'auto' }}
              aria-label="Dismiss"
              onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}
            >✕</button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

/**
 * `const toast = useToast(); toast({ title: 'Saved', tone: 'good' })`
 * Safe to call outside a provider — it simply does nothing.
 */
export function useToast(): (t: ToastInput) => void {
  const push = useContext(Ctx);
  return push ?? (() => undefined);
}
